import { APIAlias, Alias, apiAliasToAlias } from "../../apiResponse/alias";
import {
  APIUserAddress,
  apiUserAddressToUserAddress,
  UserAddress
} from "../userAddress/user-address";
import {
  OnsipAPIArray,
  onsipApiArrayToArray,
  OnsipAPIBoolean,
  onsipApiBooleanToBoolean
} from "../../apiResponse/xml-json";

interface ApiAssignedUserAddress {
  Line?: string;
  CallsPerLine?: string;
  UserAddress: APIUserAddress;
  UserAliases: OnsipAPIArray<"UserAlias", APIAlias>;
  BusyLampField?: {
    Line: string;
    SubscriberLine: string;
  };
}

export interface AssignedUserAddress {
  line?: string;
  callsPerLine?: string;
  userAddress: UserAddress;
  userAliases: Array<Alias>;
  busyLampField?: {
    line: string;
    subscriberLine: string;
  };
}

export interface ApiPhone {
  PhoneId: string;
  AccountId: string;
  OrganizationId: string;
  Make: string;
  Model: string;
  MacAddress: string;
  GmtOffset: string;
  Directory: OnsipAPIBoolean;
  EasyTransfer: OnsipAPIBoolean;
  NatKeepalive: OnsipAPIBoolean;
  WebInterface: OnsipAPIBoolean;
  Intercom: OnsipAPIBoolean;
  ManualWebPassword: OnsipAPIBoolean;
  OrganizationWebPassword: OnsipAPIBoolean;
  EndToEndEncryption: OnsipAPIBoolean;
  WebPassword: string;
  EncryptionKey: string;
  Digitmap: string;
  AssignedUserAddresses: OnsipAPIArray<"AssignedUserAddress", ApiAssignedUserAddress>;
}

export interface Phone {
  phoneId: string;
  accountId: string;
  organizationId: string;
  make: string;
  model: string;
  macAddress: string;
  gmtOffset: string;
  directory: boolean;
  easyTransfer: boolean;
  natKeepalive: boolean;
  webInterface: boolean;
  intercom: boolean;
  manualWebPassword: boolean;
  organizationWebPassword: boolean;
  endToEndEncryption: boolean;
  webPassword: string;
  encryptionKey: string;
  digitmap: string;
  assignedUserAddresses: Array<AssignedUserAddress>;
}

export function apiPhoneToPhone(apiPhone: ApiPhone): Phone {
  return {
    phoneId: apiPhone.PhoneId,
    accountId: apiPhone.AccountId,
    organizationId: apiPhone.OrganizationId,
    make: apiPhone.Make,
    model: apiPhone.Model,
    macAddress: apiPhone.MacAddress,
    gmtOffset: apiPhone.GmtOffset,
    directory: onsipApiBooleanToBoolean(apiPhone.Directory),
    easyTransfer: onsipApiBooleanToBoolean(apiPhone.EasyTransfer),
    natKeepalive: onsipApiBooleanToBoolean(apiPhone.NatKeepalive),
    webInterface: onsipApiBooleanToBoolean(apiPhone.WebInterface),
    intercom: onsipApiBooleanToBoolean(apiPhone.Intercom),
    manualWebPassword: onsipApiBooleanToBoolean(apiPhone.ManualWebPassword),
    organizationWebPassword: onsipApiBooleanToBoolean(apiPhone.OrganizationWebPassword),
    endToEndEncryption: onsipApiBooleanToBoolean(apiPhone.EndToEndEncryption),
    webPassword: apiPhone.WebPassword,
    encryptionKey: apiPhone.EncryptionKey,
    digitmap: apiPhone.Digitmap,
    assignedUserAddresses: onsipApiArrayToArray(
      apiPhone.AssignedUserAddresses,
      "AssignedUserAddress"
    ).map(address => {
      let assignedAddress: AssignedUserAddress = {
        line: address.Line,
        callsPerLine: address.CallsPerLine,
        userAddress: apiUserAddressToUserAddress(address.UserAddress),
        userAliases: onsipApiArrayToArray(address.UserAliases, "UserAlias").map(apiAliasToAlias)
      };
      if (address.BusyLampField) {
        assignedAddress = {
          ...assignedAddress,
          ...{
            busyLampField: {
              line: address.BusyLampField.Line,
              subscriberLine: address.BusyLampField.SubscriberLine
            }
          }
        };
      }
      return assignedAddress;
    })
  };
}

export interface PhoneEditRequestParams {
  PhoneId: string;
  Make: string;
  Model: string;
  GmtOffset: number;
  Directory?: boolean;
  EasyTransfer?: boolean;
  NatKeepalive?: boolean;
  WebInterface?: boolean;
  Intercom?: boolean;
  ManualWebPassword?: boolean;
  OrganizationWebPassword?: boolean;
  EndToEndEncryption?: boolean;
  Digitmap?: string;
  OrganizationId?: string;
}

export interface PhoneAddRequestParams extends Omit<PhoneEditRequestParams, "PhoneId"> {
  MacAddress: string;
  OrganizationId?: string;
}

export interface PhoneAssignParams {
  PhoneId: string;
  /** this is the user's aor/sip adddress */
  Address: string;
  Line: number;
  BusyLampField?: boolean;
  // the following two params are only used if BLF is true
  /** subscriberLine is required if busyLampField is true */
  SubscriberLine?: number;
  CallsPerLine?: number;
}

export interface PhoneDeassignParams {
  PhoneId: string;
  /** if address is provided, then only the provided address is deassigned. Otherwise, all addresses are deassigned */
  Address?: string;
  /** if BLF is true, then remove all BLF addresses. If Address and BLF is true, remove BLF address of given address */
  BusyLampField?: boolean;
}
